import { useState, useEffect, useMemo, useRef } from "react";
import { Label } from "@/components/ui/label";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { geometriesApi, objectsApi } from "@/services/api-v2.service";
import { objectCardApi } from "@/services/card-api.service";
import {
  type ObjectType,
  type Organization,
  type GeoObject,
  GeoObjectCreate,
  GeoObjectUpdate,
} from "@/types/api-v2";
import { buildObjectTypesHierarchy, flattenObjectTypesHierarchy } from "@/utils/objectTypesHierarchy";
import { buildOrganizationsHierarchy, flattenOrganizationsHierarchy } from "@/utils/organizationsHierarchy";
import {
  DynamicCardRenderer,
  type DynamicCardRendererHandle,
} from "@/components/card/DynamicCardRenderer";
import { cn } from "@/lib/utils";
import type { ObjectCreateInitialGeometryPayload } from "@/types/card-template";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ApiClientError } from "@/lib/geometry-api-errors";
import { GarAddressField } from "@/components/GarAddressField";

interface ObjectFormProps {
  objectTypes: ObjectType[];
  organizations: Organization[];
  onCancel: () => void;
  /** После успешного создания объекта (не редактирования): например привязка контура из режима «сначала карта». */
  onCreateSuccess?: (created: GeoObject) => void;
  /** Если задано и включён шаблон карточки — уходит в POST /objects/with-card как initial_geometry. */
  initialGeometryFromMapFirst?: ObjectCreateInitialGeometryPayload | null;
  editObject?: {
    id: number;
    name: string;
    fullName?: string | null;
    kls_id: number;
    organizationId: number;
    status: string;
    description?: string | null;
    address?: string | null;
    gar_uuid?: string | null;
    area?: number | null;
  };
}

export default function ObjectForm({
  objectTypes,
  organizations,
  onCancel,
  onCreateSuccess,
  initialGeometryFromMapFirst = null,
  editObject,
}: ObjectFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const cardCreateRef = useRef<DynamicCardRendererHandle>(null);

  const [name, setName] = useState("");
  const [fullName, setFullName] = useState("");
  const [klsId, setKlsId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [status, setStatus] = useState("active");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [garUuid, setGarUuid] = useState<string | null>(null);
  const [area, setArea] = useState("");

  const isCreate = !editObject;

  const draftQuery = useQuery({
    queryKey: ["object-card-draft", klsId, organizationId],
    queryFn: () =>
      objectCardApi.getCardDraft(Number(klsId), Number(organizationId)),
    enabled: isCreate && !!klsId && !!organizationId,
    staleTime: 30_000,
  });

  const geometryCompatibleQuery = useQuery({
    queryKey: [
      "geometry-compatible-object-kls",
      organizationId,
      initialGeometryFromMapFirst?.geometry_kls_id,
    ],
    queryFn: () =>
      geometriesApi.compatibleObjectTypes({
        organization_id: Number(organizationId),
        geometry_semantic_kls_id: initialGeometryFromMapFirst!.geometry_kls_id,
      }),
    enabled:
      isCreate &&
      !!initialGeometryFromMapFirst &&
      !!organizationId &&
      !Number.isNaN(Number(organizationId)),
    staleTime: 60_000,
  });

  const showTemplateBody =
    isCreate &&
    !!klsId &&
    !!organizationId &&
    draftQuery.isSuccess &&
    draftQuery.data?.template != null;

  const draftLoading =
    isCreate && !!klsId && !!organizationId && draftQuery.isFetching;

  useEffect(() => {
    if (editObject) {
      setName(editObject.name || "");
      setFullName(editObject.fullName || "");
      setKlsId(editObject.kls_id ? String(editObject.kls_id) : "");
      setOrganizationId(editObject.organizationId ? String(editObject.organizationId) : "");
      setStatus(editObject.status || "active");
      setDescription(editObject.description || "");
      setAddress(editObject.address || "");
      setGarUuid(editObject.gar_uuid || null);
      setArea(editObject.area ? String(editObject.area) : "");
    } else {
      setName("");
      setFullName("");
      setKlsId("");
      setOrganizationId("");
      setStatus("active");
      setDescription("");
      setAddress("");
      setGarUuid(null);
      setArea("");
    }
  }, [editObject]);

  const [nameError, setNameError] = useState("");
  const [typeError, setTypeError] = useState("");
  const [orgError, setOrgError] = useState("");

  const objectTypesHierarchy = useMemo(() => {
    if (!objectTypes || objectTypes.length === 0) return [];
    const hierarchy = buildObjectTypesHierarchy(objectTypes);
    return flattenObjectTypesHierarchy(hierarchy);
  }, [objectTypes]);

  const organizationsHierarchy = useMemo(() => {
    if (!organizations || organizations.length === 0) return [];
    const hierarchy = buildOrganizationsHierarchy(organizations);
    return flattenOrganizationsHierarchy(hierarchy);
  }, [organizations]);

  const objectTypeOptions = useMemo((): ComboboxOption[] => {
    if (objectTypesHierarchy.length > 0) {
      return objectTypesHierarchy.map((type) => ({
        value: type.id.toString(),
        label: type.name_short || type.name,
        shortLabel: type.name_short,
        fullLabel: type.name,
        indentLevel: type.indentLevel,
      }));
    }
    return objectTypes.map((type) => ({
      value: type.id.toString(),
      label: type.name_short || type.name,
      shortLabel: type.name_short,
      fullLabel: type.name,
      indentLevel: 0,
    }));
  }, [objectTypesHierarchy, objectTypes]);

  const filteredObjectTypeOptions = useMemo(() => {
    if (!isCreate || !initialGeometryFromMapFirst) return objectTypeOptions;
    const d = geometryCompatibleQuery.data;
    if (!d || d.unrestricted) return objectTypeOptions;
    if (d.object_kls_ids.length === 0) return objectTypeOptions;
    const allow = new Set(d.object_kls_ids);
    return objectTypeOptions.filter((o) => allow.has(Number(o.value)));
  }, [isCreate, initialGeometryFromMapFirst, objectTypeOptions, geometryCompatibleQuery.data]);

  useEffect(() => {
    if (!klsId || !geometryCompatibleQuery.data) return;
    const d = geometryCompatibleQuery.data;
    if (d.unrestricted || d.object_kls_ids.length === 0) return;
    if (!d.object_kls_ids.includes(Number(klsId))) {
      setKlsId("");
    }
  }, [geometryCompatibleQuery.data, klsId]);

  const organizationOptions = useMemo((): ComboboxOption[] => {
    if (organizationsHierarchy.length > 0) {
      return organizationsHierarchy.map((org) => ({
        value: org.id.toString(),
        label: org.name_short || org.name,
        shortLabel: org.name_short,
        fullLabel: org.name,
        indentLevel: org.indentLevel,
      }));
    }
    return organizations.map((org) => ({
      value: org.id.toString(),
      label: org.name_short || org.name,
      shortLabel: org.name_short,
      fullLabel: org.name,
      indentLevel: 0,
    }));
  }, [organizationsHierarchy, organizations]);

  const validateKlsOrg = () => {
    let ok = true;
    if (!klsId) {
      setTypeError("Выберите тип объекта");
      ok = false;
    } else {
      setTypeError("");
    }
    if (!organizationId) {
      setOrgError("Выберите орган управления");
      ok = false;
    } else {
      setOrgError("");
    }
    return ok;
  };

  const validateLegacyForm = () => {
    let isValid = true;
    if (!name || name.length < 3) {
      setNameError("Название должно содержать не менее 3 символов");
      isValid = false;
    } else {
      setNameError("");
    }
    if (!validateKlsOrg()) isValid = false;
    return isValid;
  };

  const validateTemplateCreate = () => {
    if (!validateKlsOrg()) return false;
    const payload = cardCreateRef.current?.getPayload?.();
    if (!payload) {
      toast({
        title: "Ошибка",
        description: "Не удалось собрать данные карточки",
        variant: "destructive",
      });
      return false;
    }
    const nameField = payload.object_values.find((v) => v.field_key === "obj_name");
    if (nameField) {
      const t = (nameField.value_text ?? "").trim();
      if (t.length > 0 && t.length < 3) {
        toast({
          title: "Проверьте название",
          description: "Название объекта должно содержать не менее 3 символов",
          variant: "destructive",
        });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editObject?.id) {
      if (!validateLegacyForm()) return;
      setIsLoading(true);
      try {
        const updateData: GeoObjectUpdate = {
          name,
          full_name: fullName || undefined,
          kls_id: parseInt(klsId, 10),
          organization_id: parseInt(organizationId, 10),
          status,
          description: description || undefined,
          address: address || undefined,
          gar_uuid: garUuid,
          area: area ? parseFloat(area) : undefined,
        };
        await objectsApi.update(editObject.id, updateData);
        toast({ title: "Объект обновлен", description: "Изменения успешно сохранены" });
        queryClient.invalidateQueries({ queryKey: ["objects"] });
        queryClient.invalidateQueries({ queryKey: ["objects", editObject.id] });
        queryClient.invalidateQueries({ queryKey: ["objects", editObject.id, "geometries"] });
        queryClient.invalidateQueries({ queryKey: ["objects", editObject.id, "files"] });
        onCancel();
      } catch {
        toast({
          title: "Ошибка",
          description: "Не удалось обновить объект",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!validateKlsOrg()) return;

    if (showTemplateBody) {
      if (!validateTemplateCreate()) return;
      setIsLoading(true);
      try {
        const payload = cardCreateRef.current?.getPayload?.();
        if (!payload) throw new Error("no payload");
        const created = await objectCardApi.createWithCard({
          kls_id: parseInt(klsId, 10),
          organization_id: parseInt(organizationId, 10),
          object_values: payload.object_values,
          geometries: [],
          ...(initialGeometryFromMapFirst
            ? { initial_geometry: initialGeometryFromMapFirst }
            : {}),
        });
        toast({ title: "Объект создан", description: "Новый объект успешно добавлен" });
        queryClient.invalidateQueries({ queryKey: ["objects"] });
        if (initialGeometryFromMapFirst) {
          void queryClient.invalidateQueries({ queryKey: ["objects", created.id, "geometries"] });
        }
        onCreateSuccess?.(created);
        onCancel();
      } catch (err) {
        if (err instanceof ApiClientError && err.validation_errors.length > 0) {
          cardCreateRef.current?.showValidationErrors?.(
            err.validation_errors.map((item) => ({
              field_key: item.field_key,
              message: item.message,
              geometry_id: item.geometry_id,
              applies_to: item.applies_to,
              code: item.code ?? "VALIDATION",
            })),
          );
        }
        const msg = err instanceof Error ? err.message : "Не удалось создать объект";
        toast({ title: "Ошибка", description: msg, variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!validateLegacyForm()) return;
    setIsLoading(true);
    try {
      const createData: GeoObjectCreate = {
        name,
        full_name: fullName || undefined,
        kls_id: parseInt(klsId, 10),
        organization_id: parseInt(organizationId, 10),
        status,
        description: description || undefined,
        address: address || undefined,
        gar_uuid: garUuid || undefined,
        area: area ? parseFloat(area) : undefined,
      };
      const created = await objectsApi.create(createData);
      if (initialGeometryFromMapFirst) {
        await objectsApi.addGeometry(created.id, {
          object_id: created.id,
          name: initialGeometryFromMapFirst.name,
          description: initialGeometryFromMapFirst.description,
          geojson: initialGeometryFromMapFirst.geojson,
          type: initialGeometryFromMapFirst.type,
          style: initialGeometryFromMapFirst.style as Record<string, unknown>,
          parent_id: initialGeometryFromMapFirst.parent_id,
          geometry_kls_id: initialGeometryFromMapFirst.geometry_kls_id,
          geometry_primitive_kls_id: initialGeometryFromMapFirst.geometry_primitive_kls_id,
          display_order: initialGeometryFromMapFirst.display_order,
        });
      }
      toast({ title: "Объект создан", description: "Новый объект успешно добавлен" });
      queryClient.invalidateQueries({ queryKey: ["objects"] });
      if (initialGeometryFromMapFirst) {
        void queryClient.invalidateQueries({ queryKey: ["objects", created.id, "geometries"] });
      }
      onCreateSuccess?.(created);
      onCancel();
    } catch {
      toast({
        title: "Ошибка",
        description: "Не удалось создать объект",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogContent
      className={cn(
        "max-h-[90vh] overflow-y-auto",
        showTemplateBody ? "sm:max-w-3xl" : "sm:max-w-[625px]"
      )}
    >
      <DialogHeader>
        <DialogTitle className="text-xl font-bold">
          {editObject ? "Редактирование объекта" : "Создание нового объекта"}
        </DialogTitle>
        <p className="text-sm text-muted-foreground">
          {editObject
            ? "Измените информацию об объекте"
            : "Заполните информацию об объекте"}
        </p>
      </DialogHeader>

      {isCreate && initialGeometryFromMapFirst ? (
        <Alert>
          <AlertDescription>
            После сохранения к объекту будет добавлен контур, нарисованный на карте (как первая геометрия).
          </AlertDescription>
        </Alert>
      ) : null}

      {isCreate &&
      initialGeometryFromMapFirst &&
      organizationId &&
      geometryCompatibleQuery.data?.no_matching_geometry_rule &&
      !geometryCompatibleQuery.data.unrestricted ? (
        <Alert variant="destructive">
          <AlertDescription>
            Нет опубликованного шаблона с правилом для выбранной семантики геометрии и органа управления. Тип объекта
            можно выбрать вручную; при сохранении возможна ошибка, если шаблон или лимиты геометрий не подходят.
          </AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2 md:col-span-2">
            <Label>Тип объекта (назначение) *</Label>
            <Combobox
              options={filteredObjectTypeOptions}
              value={klsId || undefined}
              onValueChange={(value) => {
                setKlsId(value || "");
                setTypeError("");
              }}
              placeholder="Выберите тип объекта"
              searchPlaceholder="Поиск типа..."
              emptyText="Типы не найдены"
              allowClear={false}
              disabled={!!editObject}
            />
            {typeError && <p className="text-sm text-red-500">{typeError}</p>}
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label>Орган управления *</Label>
            <Combobox
              options={organizationOptions}
              value={organizationId || undefined}
              onValueChange={(value) => {
                setOrganizationId(value || "");
                setOrgError("");
              }}
              placeholder="Выберите орган управления"
              searchPlaceholder="Поиск органа управления..."
              emptyText="Органы управления не найдены"
              allowClear={false}
              disabled={!!editObject}
            />
            {orgError && <p className="text-sm text-red-500">{orgError}</p>}
          </div>
        </div>

        {isCreate && draftLoading && (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {isCreate && showTemplateBody && draftQuery.data && (
          <div className="border rounded-md p-3 bg-muted/30">
            <DynamicCardRenderer
              key={`${klsId}-${organizationId}`}
              ref={cardCreateRef}
              objectId={0}
              initialCardOverride={draftQuery.data}
              creationMode
              mode="edit"
              showBottomActionBar={false}
            />
          </div>
        )}

        {(editObject || (isCreate && !showTemplateBody && !draftLoading)) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Название объекта *</Label>
              <Input
                id="name"
                placeholder="Введите название"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {nameError && <p className="text-sm text-red-500">{nameError}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">Расширенное полное наименование</Label>
              <Input
                id="fullName"
                placeholder="Введите расширенное полное наименование"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Статус</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="active">Активный</SelectItem>
                  <SelectItem value="archived">Архивный</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <GarAddressField
              address={address}
              garUuid={garUuid}
              onAddressChange={setAddress}
              onGarUuidChange={(uuid, formatted) => {
                setGarUuid(uuid);
                setAddress(formatted);
              }}
            />

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                placeholder="Введите описание объекта"
                className="h-24 resize-none"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="area">Площадь (м²)</Label>
              <Input
                id="area"
                type="number"
                placeholder="0.00"
                value={area}
                onChange={(e) => setArea(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
            Отмена
          </Button>
          <Button type="submit" disabled={isLoading || (isCreate && draftLoading)}>
            {isLoading
              ? editObject
                ? "Сохранение..."
                : "Создание..."
              : editObject
                ? "Сохранить"
                : "Создать"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
